import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLenis } from "@/App";
import { EASE } from "@/components/Reveal";
import Logo from "@/components/Logo";
import Magnetic from "@/components/Magnetic";
import { CONTACT } from "@/data/services";

const LINKS = [
  { to: "/", label: "Home", testId: "nav-link-home" },
  { to: "/services", label: "Services", testId: "nav-link-services" },
  { to: "/packages", label: "Packages", testId: "nav-link-packages" },
  { to: "/work", label: "Work", testId: "nav-link-work" },
  { to: "/about", label: "About", testId: "nav-link-about" },
  { to: "/contact", label: "Contact", testId: "nav-link-contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenisRef = useLenis();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const lenis = lenisRef?.current;
    if (open) lenis?.stop();
    else lenis?.start();
  }, [open, lenisRef]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled ? "border-b border-white/10 bg-black/70 backdrop-blur-xl" : "border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 lg:px-12">
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <Logo size="sm" />
          </Link>

          <nav className="hidden items-center gap-9 lg:flex" aria-label="Primary">
            {LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={link.testId}
                className={({ isActive }) =>
                  `group relative font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                    isActive ? "text-paper" : "text-zinc-500 hover:text-paper"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    <span
                      className={`absolute -bottom-1.5 left-0 h-px bg-brand transition-all duration-300 ${
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <Magnetic strength={0.25} className="hidden lg:inline-block">
              <Link
                to="/contact"
                data-testid="nav-cta"
                className="group flex items-center gap-2 rounded-full btn-ribbon px-5 py-2.5 font-mono text-xs font-medium uppercase tracking-[0.15em] text-white transition-colors duration-300 "
              >
                Plan a Campaign
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Magnetic>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(!open)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-paper lg:hidden"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 flex flex-col justify-center bg-ink/95 px-8 backdrop-blur-2xl lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <nav className="flex flex-col gap-2" aria-label="Mobile">
              {LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, y: 28 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.6, ease: EASE }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`mobile-${link.testId}`}
                    className={({ isActive }) =>
                      `font-display text-5xl font-bold uppercase tracking-tight ${isActive ? "text-brand" : "text-paper"}`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.p
              className="mt-12 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {CONTACT.phone} · {CONTACT.email}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
